/**
 * PatientHistorySummary — Displays PHI-safe patient history summary.
 *
 * Shows recent reports with modality, body part, and diagnosis summary.
 * Safe for UI display and LLM context injection (no PHI).
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import patientService, { PatientHistorySummaryEntry } from '../services/patientService';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/reduxHooks';
import { typography } from '~/theme';

interface PatientHistorySummaryProps {
  patientId: string | null;
  limit?: number;
}

const PatientHistorySummary: React.FC<PatientHistorySummaryProps> = ({
  patientId,
  limit = 5,
}) => {
  const [history, setHistory] = useState<PatientHistorySummaryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const hardLockEnabled = useAppSelector((state) => state.report.config?.hardLockEnabled ?? false);

  useEffect(() => {
    if (!patientId) {
      setHistory([]);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await patientService.getPatientHistorySummary(patientId, limit);
        if (!active) return;
        setHistory(response.history);
      } catch (err: unknown) {
        if (!active) return;
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr.response?.status === 404) {
          setHistory([]);
        } else {
          console.error('Failed to load patient history:', err);
          setError('Failed to load patient history');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [patientId, limit]);

  if (!patientId) {
    return null;
  }

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={typography.label.fontWeight}>
            Patient History
          </Typography>
          <CircularProgress size={14} sx={{ ml: 'auto' }} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={typography.label.fontWeight}>
            Patient History
          </Typography>
        </Box>
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  if (history.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={typography.label.fontWeight}>
            Patient History
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No prior reports found
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HistoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" color="primary.dark" fontWeight={typography.label.fontWeight}>
          Patient History
        </Typography>
        <Chip
          label={`${history.length} ${history.length === 1 ? 'report' : 'reports'}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ ml: 'auto', fontSize: typography.caption.fontSize, height: 20 }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {history.map((entry, index) => (
          <Box
            key={index}
            sx={{
              p: 1,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              borderLeft: 3,
              borderColor: 'primary.light',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {entry.date && (
                <Chip
                  icon={<CalendarTodayIcon sx={{ fontSize: 10 }} aria-hidden="true" />}
                  label={entry.date}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.label.fontWeight }}
                />
              )}
              {entry.modality && (
                <Chip
                  label={entry.modality}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.label.fontWeight }}
                />
              )}
              {entry.body_part && (
                <Chip
                  icon={<LocalHospitalIcon sx={{ fontSize: 10 }} aria-hidden="true" />}
                  label={entry.body_part}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: typography.overline.fontSize }}
                />
              )}
              {entry.report_status && (
                <Chip
                  label={entry.report_status === 'final' ? (hardLockEnabled ? 'Generated' : 'Finalized') : entry.report_status === 'review' ? 'Review' : 'Draft'}
                  size="small"
                  color={entry.report_status === 'final' ? 'success' : entry.report_status === 'review' ? 'info' : 'warning'}
                  variant="outlined"
                  sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.label.fontWeight }}
                />
              )}
              {entry.session_id && (
                <Tooltip title={entry.report_status === 'final' ? 'View finalized report' : 'Open report'} arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const mode = entry.report_status === 'final' ? 'view' : 'edit';
                      navigate(`/report/edit?session=${entry.session_id}&mode=${mode}`);
                    }}
                    sx={{ ml: 'auto', p: 0.25 }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: typography.caption.fontSize }}>
              {entry.summary}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default PatientHistorySummary;
